/**
 * The organization connector list — the client side of /connector-library
 * (managing an organization's list: group admins and super admins) and of
 * /chatbots/<id>/connector-library (using it on an agent: anyone who may
 * edit the agent).
 *
 * Each organization sees its own entries and the ones it inherits from the
 * organizations above it. For itself and those under it, a manager may
 * change an inherited entry's defaults (a layer), remove it, or restore the
 * parent's version. Agent connectors added from the list FOLLOW it until
 * someone edits them on the agent. A shared credential is write-only:
 * responses say whether one exists, never what it is.
 */

import api from '@services/api';
import type { ApiAuthType, ApiConnector, ApiOperation, ReadSpecResult } from '@modules/chatbots/services/apiConnectorService';
import type { McpConnector } from '@modules/chatbots/services/mcpConnectorService';
import type { SignInMode } from '@modules/chatbots/utils/signIn';

export type LibraryKind = 'mcp' | 'api';

/** One change to an entry: who, where, and what it did to agents. */
export interface LibraryChange {
  action: 'added' | 'edited' | 'removed' | 'restored' | 'deleted' | 'refreshed' | 'refresh_failed'
    | 'availability' | 'unhidden' | 'signin' | 'acknowledged';
  at: string | null;
  /** The person — named only for a change made at a level the viewer manages. */
  by: string | null;
  organization: string | null;
  detail: {
    fields?: string[];
    updated?: number;
    reset?: number;
    detached?: number;
    keys_needed?: number;
    force?: boolean;
    /** A risk acknowledgement (item 69): a connector added by hand, or tools that change data switched on. */
    risk?: 'custom' | 'write';
    tools?: string[];
    /** An availability change: the owner's choice and how many organizations it now covers. */
    mode?: AvailabilityMode;
    organizations?: number;
    added?: number;
    removed?: number;
  };
}

/** Shown to everyone who may see the entry; never carries a credential. */
export interface LibraryEntrySummary {
  entry_id: string;
  /** The organization that owns the entry. */
  tenant_id: string;
  kind: LibraryKind;
  name: string;
  slug: string;
  description: string;
  version: number;
  revision: number;
  needs_credential: boolean;
  has_shared_credential: boolean;
  /** How it signs in (item 65A) — stored, or what the key settings mean. */
  signin_mode?: 'none' | 'shared' | 'per_agent' | 'per_person_oauth' | 'per_person_key';
  /** sign_in_needed: each person signs in, and nobody listed its tools with their own sign-in yet. */
  status: 'connected' | 'error' | 'sign_in_needed';
  last_error: string | null;
  updated_at: string | null;
  url?: string | null;
  auth_header_name?: string | null;
  tool_count?: number;
  active_tool_count?: number;
  base_url?: string | null;
  spec_url?: string | null;
  auth_type?: ApiAuthType;
  auth_meta?: { in?: 'header' | 'query'; name?: string };
  operation_count?: number;
  enabled_operation_count?: number;
}

/** A card on the list page, as one organization sees it. */
export interface LibraryCard extends LibraryEntrySummary {
  owner_name: string | null;
  /** Whose version "Restore" goes back to: the nearest level above that set it. */
  parent_name: string | null;
  inherited: boolean;
  defined_at_name: string | null;
  /** This organization changed it for itself and those under it. */
  edited_here: boolean;
  /** This organization removed it for itself and those under it. */
  removed_here: boolean;
  /** Who gets it in the managed organization's part of the tree:
      `organizations` of `total` (the managed one included), `count` below it;
      `only` = its owner chose "Only these organizations". */
  available_to: { name: string | null; count: number; organizations: number; total: number; only: boolean };
  usage: LibraryCardUsage;
  last_change: LibraryChange | null;
  edit_version: number;
  /** Where the row is managed: its owner when the owner is shown, else the
      organization nearest the top of the choice that gets it (item 83). It
      opens there, and every count and action on the row is that level's. */
  level: LibraryLevel | null;
  /** Who added it, with the organization above it (item 83's scope list only). */
  owner?: { tenant_id: string; name: string | null; parent_name: string | null };
}

/** An organization a list row is managed at. */
export interface LibraryLevel { tenant_id: string; name: string | null; descendant_count: number }

/** Agents in the managed part of the tree that use an entry. */
export interface LibraryUsage {
  total: number;
  following: number;
  customized: number;
  /** Agents that cannot work until someone enters their own key. */
  needs_key: number;
}

/** A list row's usage: its agents, and the organizations they are in —
    within what the page counts (the managed part of the tree, or the one
    organization it is narrowed to). */
export interface LibraryCardUsage extends LibraryUsage {
  organizations: number;
  /** The first of them, the most agents first; the rest are only counted. */
  organization_names: string[];
}

/** The fields a lower organization may change on an inherited entry. */
export type LayerField =
  | 'name' | 'description' | 'disabled_tools'
  | 'operation_settings' | 'instructions' | 'cache_ttl_seconds' | 'max_result_chars';

/** One entry's defaults, in the shapes the connector editors render. */
export interface LibraryEntryDetail extends LibraryEntrySummary {
  created_at: string | null;
  refreshed_at: string | null;
  // MCP
  transport?: string;
  server_name?: string | null;
  server_version?: string | null;
  server_instructions?: string | null;
  tools?: { name: string; description: string; enabled: boolean; writes?: boolean }[];
  // API
  spec_title?: string | null;
  spec_version?: string | null;
  instructions?: string;
  cache_ttl_seconds?: number | null;
  max_result_chars?: number | null;
  operations?: ApiOperation[];
}

/** An entry as the managing organization's editor sees it. */
export interface LibraryEntryEditable extends LibraryEntryDetail {
  owner_name: string | null;
  inherited: boolean;
  editable_fields: 'all' | LayerField[];
  edited_here: boolean;
  edited_fields: string[];
  removed_here: boolean;
  parent_values: Record<string, unknown> | null;
  /** Whose version "Restore" goes back to: the nearest level above that set it. */
  parent_name: string | null;
  edit_version: number;
  last_change: LibraryChange | null;
  usage: LibraryUsage;
}

/** What "Check" found on an MCP server — nothing is saved. */
export interface McpCheckResult {
  url: string;
  server_name: string | null;
  server_version: string | null;
  tools: { name: string; description: string }[];
  /** The server asks each person to sign in with their own account (item 65A). */
  sign_in?: { mode: 'per_person_oauth'; issuer: string | null };
}

/** A ready-made connector a super admin may put on the organization's list. */
export interface LibraryStarter {
  template_id: string;
  name: string;
  description: string;
  kind: LibraryKind;
  /** API: the actions that start on. */
  operations?: number;
  /** MCP: its address, how agents sign in, and the reading tools that start on. */
  url?: string;
  signin_mode?: string;
  /** The manager pastes the service's token when placing it ("shared"). */
  needs_credential?: boolean;
  credential_scheme?: string | null;
  token_help_url?: string | null;
  tools_on?: number;
  /** This organization's list already has one made from it. */
  on_list: boolean;
}

/** The list for organizations chosen in the filter (item 83): every connector
    in them, each with its owner and the level its actions act at. */
export interface LibraryScopePage {
  scope: { organizations: number; picks: { tenant_id: string; name: string; below: boolean; below_count: number }[] };
  entries: (LibraryCard & { level: LibraryLevel; owner: NonNullable<LibraryCard['owner']> })[];
  kinds: LibraryKind[];
}

/** What a change did — or would do — to agents. */
export interface ChangeOutcome {
  updated: number;
  reset: number;
  detached: number;
  keys_needed: number;
  /** A refresh of a ready-made: the tools new on the server that start off. */
  new_tools_off?: string[];
}

export interface ChangePreview {
  updates: number;
  detaches: number;
  keys_needed: number;
  /** Customized agents in the managing organization's part of the tree. */
  customized: { chatbot_id: string; chatbot_name: string | null; tenant_id: string }[];
  /** The agents that would lose it, by organization; `needs_key`: its copy
      would need its own key (the organization's shared one never goes with it). */
  detached: { chatbot_id: string; chatbot_name: string | null; tenant_id: string; tenant_name: string | null;
              needs_key: boolean }[];
  detached_organizations: number;
}

export type PreviewAction = 'save' | 'remove' | 'restore' | 'availability';

export type AvailabilityMode = 'all' | 'only';

/** An organization's state for one entry: it gets it ('on'), its owner did
    not choose it, its own manager removed it, or a level above did. */
export type OrgState = 'on' | 'not_offered' | 'hidden' | 'hidden_above';

/** What flipping an organization's switch does — decided by the server's one rule. */
export interface SwitchPlan {
  action: 'set_add' | 'set_remove' | 'hide' | 'unhide' | null;
  reason: 'last_one' | 'owner_all' | 'not_offered' | 'hidden_above' | null;
}

export interface AvailabilityOrg {
  tenant_id: string;
  /** null for the organization the view is opened on. */
  parent_id: string | null;
  name: string;
  /** In the owner's choice ("All" chooses every one). */
  chosen: boolean;
  state: OrgState;
  /** The level that removed it, for 'hidden' / 'hidden_above'. */
  hidden_at_name: string | null;
  /** Its defaults were changed there. */
  changed_here: boolean;
  agents: number;
  /** People there signed in with their own account (per-person sign-in). */
  people: number;
  switch: SwitchPlan;
}

/** Which organizations get an entry, from one level. */
export interface Availability {
  mode: AvailabilityMode;
  /** The owner's choice under "Only these organizations". */
  tenant_ids: string[];
  /** The owner's own page edits the choice; a lower level only switches organizations off. */
  can_choose: boolean;
  owner_name: string | null;
  version: number;
  available: number;
  total: number;
  organizations: AvailabilityOrg[];
}

/** One entry in "Connectors available here". */
export interface OrgConnector extends LibraryEntrySummary {
  owner_name: string | null;
  inherited: boolean;
  state: OrgState;
  hidden_at_name: string | null;
  agents: number;
  switch: SwitchPlan;
}

/** An entry of the agent's organization's effective list. */
export interface PickerEntry extends LibraryEntrySummary {
  owner_name: string | null;
  defined_at_name: string | null;
  added: boolean;
}

export interface PickerResult {
  entries: PickerEntry[];
  can_manage: boolean;
  manage_tenant_id: string | null;
}

export interface AgentUsingEntry {
  chatbot_id: string;
  chatbot_name: string;
  tenant_id: string;
  tenant_name: string;
  state: 'following' | 'customized';
}

/** On an agent's connector that came from the list: its status and what can be done. */
export interface ConnectorLibraryStatus {
  entry_id: string;
  state: 'following' | 'customized' | 'detached' | null;
  /** shared = follows the list; customized = edited here; own = this agent only. */
  chip: 'shared' | 'customized' | 'own';
  /** The organization whose settings it carries. */
  organization: string | null;
  /** Whether the entry is still on this agent's organization's list. */
  available: boolean;
  /** A one-time notice: why it stopped being shared. */
  notice: 'removed' | 'deleted' | 'unavailable' | 'not_offered' | null;
  uses_shared_key: boolean;
  needs_key: boolean;
  /** Who signs in, as the list sets it (each person's account or key is not the agent's to choose). */
  signin_mode?: SignInMode | null;
  /** The shape of the credential asked for — never a value. */
  auth_header_name?: string | null;
  auth_type?: ApiAuthType | null;
  auth_meta?: { in?: 'header' | 'query'; name?: string } | null;
}

/** What the person adding a connector enters when the organization does not share one. */
export interface LibraryCredential {
  value?: string;
  username?: string;
  password?: string;
}

const LIB = '/connector-library';
const forAgent = (chatbotId: string) => `/chatbots/${chatbotId}/connector-library`;

class ConnectorLibraryService {
  // ── managing an organization's list ─────────────────────────────────
  /** Every connector in the chosen organizations: `orgs` alone, `branches` with all under them. */
  async scopePage(scope: { orgs: string[]; branches: string[] }): Promise<LibraryScopePage> {
    const params: Record<string, string> = {};
    if (scope.orgs.length) params.orgs = scope.orgs.join(',');
    if (scope.branches.length) params.branches = scope.branches.join(',');
    const { data } = await api.get(LIB, { params });
    return { scope: data.scope, entries: data.entries || [], kinds: data.kinds || [] };
  }

  async get(entryId: string, tenantId: string): Promise<LibraryEntryEditable> {
    const { data } = await api.get(`${LIB}/${entryId}`, { params: { tenant_id: tenantId } });
    return data.entry;
  }

  async readSpec(payload: { spec_text?: string; spec_url?: string }): Promise<ReadSpecResult> {
    const { data } = await api.post(`${LIB}/read-spec`, payload);
    return data;
  }

  /** Connect to an MCP server and list its tools — nothing is saved. */
  async checkMcp(payload: { url: string; headers?: Record<string, string> }): Promise<McpCheckResult> {
    const { data } = await api.post(`${LIB}/check-mcp`, payload);
    return { url: data.url, server_name: data.server_name ?? null, server_version: data.server_version ?? null,
             tools: data.tools || [] };
  }

  /** Super admins: the ready-made connectors for the organization being managed. */
  async starters(tenantId: string): Promise<LibraryStarter[]> {
    const { data } = await api.get(`${LIB}/starters`, { params: { tenant_id: tenantId } });
    return data.starters || [];
  }

  /** `credential` — the token a service that signs in needs; used for the
      handshake and kept as the organization's shared credential, never returned. */
  async addStarter(templateId: string, tenantId: string, credential?: string): Promise<LibraryEntryEditable> {
    const { data } = await api.post(`${LIB}/starters/${encodeURIComponent(templateId)}`, {
      tenant_id: tenantId, ...(credential ? { credential } : {}),
    });
    return data.entry;
  }

  /** "Write a draft" of a connector's guide: tool definitions in, a draft out; nothing saved. */
  async draftGuide(payload: Record<string, unknown>):
    Promise<{ draft: string; model: string; cost_usd: number; cut_off: boolean }> {
    const { data } = await api.post(`${LIB}/guide-draft`, payload);
    return { draft: data.draft, model: data.model, cost_usd: data.cost_usd ?? 0, cut_off: !!data.cut_off };
  }

  async createMcp(payload: Record<string, unknown>): Promise<LibraryEntryEditable> {
    const { data } = await api.post(`${LIB}/mcp`, payload);
    return data.entry;
  }

  async createApi(payload: Record<string, unknown>): Promise<LibraryEntryEditable> {
    const { data } = await api.post(`${LIB}/api`, payload);
    return data.entry;
  }

  /** Save defaults: the entry itself (its owner) or this organization's layer. */
  async save(entryId: string, tenantId: string, payload: Record<string, unknown>,
    expectedVersion: number, force: boolean,
    /** An inherited entry: the values above that the editor was opened on. */
    expectedParent?: Record<string, unknown> | null): Promise<{ entry: LibraryEntryEditable; outcome: ChangeOutcome }> {
    const { data } = await api.patch(`${LIB}/${entryId}`, {
      ...payload, tenant_id: tenantId, expected_version: expectedVersion, force,
      ...(expectedParent ? { expected_parent: expectedParent } : {}),
    });
    return { entry: data.entry, outcome: data.outcome };
  }

  async preview(entryId: string, tenantId: string, action: PreviewAction,
    changes: Record<string, unknown> = {}): Promise<ChangePreview> {
    const { data } = await api.post(`${LIB}/${entryId}/preview`, { tenant_id: tenantId, action, changes });
    return data.preview;
  }

  /** The owner deletes it; any other organization removes it for its part of the tree. */
  async remove(entryId: string, tenantId: string): Promise<ChangeOutcome> {
    const { data } = await api.post(`${LIB}/${entryId}/remove`, { tenant_id: tenantId });
    return data.outcome;
  }

  async restore(entryId: string, tenantId: string): Promise<ChangeOutcome> {
    const { data } = await api.post(`${LIB}/${entryId}/restore`, { tenant_id: tenantId });
    return data.outcome;
  }

  async refresh(entryId: string, tenantId: string, specText?: string):
    Promise<{ entry: LibraryEntryEditable; outcome: ChangeOutcome }> {
    const { data } = await api.post(`${LIB}/${entryId}/refresh`,
      { tenant_id: tenantId, ...(specText ? { spec_text: specText } : {}) });
    return { entry: data.entry, outcome: data.outcome };
  }

  async agentsUsing(entryId: string, tenantId: string): Promise<AgentUsingEntry[]> {
    const { data } = await api.get(`${LIB}/${entryId}/agents`, { params: { tenant_id: tenantId } });
    return data.agents || [];
  }

  async history(entryId: string, tenantId: string): Promise<LibraryChange[]> {
    const { data } = await api.get(`${LIB}/${entryId}/history`, { params: { tenant_id: tenantId } });
    return data.history || [];
  }

  /** The Sign-in tab's one save (item 80): who signs in and, for a key, how
      the service expects it and — shared — the key, in one request. `key`
      carries the Settings save's own fields: an API's `auth_type` / `auth`,
      an MCP server's `headers` or `auth_header_name`. */
  async saveSignIn(entryId: string, tenantId: string, mode: string, expectedVersion: number,
    key: Record<string, unknown>, oauth?: Record<string, string>): Promise<{ entry: LibraryEntryEditable; outcome: ChangeOutcome }> {
    const { data } = await api.put(`${LIB}/${entryId}/signin-settings`, {
      tenant_id: tenantId, signin_mode: mode, expected_version: expectedVersion, ...key, ...(oauth ? { oauth } : {}),
    });
    return { entry: data.entry, outcome: data.outcome };
  }

  // ── which organizations get it ──────────────────────────────────────
  async availability(entryId: string, tenantId: string): Promise<Availability> {
    const { data } = await api.get(`${LIB}/${entryId}/availability`, { params: { tenant_id: tenantId } });
    return data.availability;
  }

  /** The owner chooses: every organization under it, or only these. */
  async setAvailability(entryId: string, tenantId: string, mode: AvailabilityMode, tenantIds: string[],
    expectedVersion: number): Promise<{ outcome: ChangeOutcome; availability: Availability }> {
    const { data } = await api.put(`${LIB}/${entryId}/availability`, {
      tenant_id: tenantId, mode, tenant_ids: mode === 'only' ? tenantIds : null, expected_version: expectedVersion,
    });
    return { outcome: data.outcome, availability: data.availability };
  }

  /** What turning it on / off there would do — nothing is written. */
  async previewSwitch(tenantId: string, entryId: string, on: boolean): Promise<ChangePreview | null> {
    const { data } = await api.post(`${LIB}/organizations/${tenantId}/available/${entryId}`, { on, preview: true });
    return data.preview ?? null;
  }

  async switchHere(tenantId: string, entryId: string, on: boolean):
    Promise<{ outcome: ChangeOutcome; connectors: OrgConnector[] }> {
    const { data } = await api.post(`${LIB}/organizations/${tenantId}/available/${entryId}`, { on });
    return { outcome: data.outcome, connectors: data.connectors || [] };
  }

  // ── using it on an agent ────────────────────────────────────────────
  async picker(chatbotId: string): Promise<PickerResult> {
    const { data } = await api.get(forAgent(chatbotId));
    return { entries: data.entries || [], can_manage: !!data.can_manage, manage_tenant_id: data.manage_tenant_id ?? null };
  }

  async agentEntry(chatbotId: string, entryId: string): Promise<LibraryEntryDetail> {
    const { data } = await api.get(`${forAgent(chatbotId)}/${entryId}`);
    return data.entry;
  }

  async addToAgent(chatbotId: string, entryId: string, options: { credential?: LibraryCredential; slug?: string } = {}):
    Promise<{ kind: LibraryKind; connector: McpConnector | ApiConnector }> {
    const { data } = await api.post(`${forAgent(chatbotId)}/${entryId}/add`, {
      ...(options.credential ? { credential: options.credential } : {}),
      ...(options.slug ? { slug: options.slug } : {}),
    });
    return { kind: data.kind, connector: data.connector };
  }

  /** "Reset to the organization's settings" / "Follow the organization's settings again". */
  async reset<T extends McpConnector | ApiConnector>(chatbotId: string, kind: LibraryKind, connectorId: string,
    credential?: LibraryCredential): Promise<T> {
    const { data } = await api.post(`${forAgent(chatbotId)}/${kind}/${connectorId}/reset`, credential ? { credential } : {});
    return data.connector;
  }

  /** This agent's own key for a connector that follows the list — not a customization. */
  async setKey<T extends McpConnector | ApiConnector>(chatbotId: string, kind: LibraryKind, connectorId: string,
    credential: LibraryCredential): Promise<T> {
    const { data } = await api.post(`${forAgent(chatbotId)}/${kind}/${connectorId}/key`, { credential });
    return data.connector;
  }

  async dismissNotice<T extends McpConnector | ApiConnector>(chatbotId: string, kind: LibraryKind,
    connectorId: string): Promise<T> {
    const { data } = await api.post(`${forAgent(chatbotId)}/${kind}/${connectorId}/dismiss`);
    return data.connector;
  }
}

export const connectorLibraryService = new ConnectorLibraryService();

/** Mirrors MAX_DESCRIPTION_CHARS in backend/modules/connector_library/services/connector_library_service.py. */
export const LIBRARY_DESCRIPTION_MAX = 500;

/** The list page for one organization, under the tenant path. */
export const manageLibraryPath = (tenantId: string): string =>
  `/admin?tab=connector-library&org=${encodeURIComponent(tenantId)}`;

/** The JSON error body a failed request came back with ({} if none). */
export interface LibraryErrorBody {
  error?: string;
  /** e.g. OAUTH_START_HOST: a sign-in started on a host it cannot return to. */
  error_code?: string;
  field?: string;
  reason?: string;
  /** 409 version conflict. */
  changed_by?: string | null;
  changed_at?: string | null;
  /** 409: the level above changed the inherited values since the editor opened. */
  changed_above?: boolean;
  /** 409 slug collision. */
  slug?: string;
  suggested?: string;
  /** A failed refresh still sends the entry back, marked "Last refresh failed". */
  entry?: LibraryEntryEditable;
}

export function errorBody(err: unknown): LibraryErrorBody {
  const data = (err as { response?: { data?: LibraryErrorBody } })?.response?.data;
  return data && typeof data === 'object' ? data : {};
}

export function errorStatus(err: unknown): number | undefined {
  return (err as { response?: { status?: number } })?.response?.status;
}

/**
 * An agent connector as a PATCH / refresh answered it: those responses carry
 * the connector's own state (`library_state`, `library_notice`) but not the
 * list status, so keep what the list said and bring the state up to date.
 * The section reloads its list afterwards for anything else that moved.
 */
export function withLibraryStatus<T extends { library?: ConnectorLibraryStatus | null;
  library_state?: string | null; library_notice?: string | null }>(previous: T, next: T): T {
  if (next.library !== undefined || !previous.library) return next;
  const state = (next.library_state ?? previous.library.state) as ConnectorLibraryStatus['state'];
  return {
    ...next,
    library: {
      ...previous.library,
      state,
      chip: state === 'following' ? 'shared' : state === 'customized' ? 'customized' : 'own',
      notice: (next.library_notice ?? null) as ConnectorLibraryStatus['notice'],
    },
  };
}
